/**
 * Simple Fire Alarm Types for NFPA 72 Validation
 */

/**
 * Represents a fire alarm device extracted from the model
 */
export interface FireAlarmDevice {
  dbId: number;
  name: string;
  family?: string;
  type?: string;
  level?: string;
  category: string;
}

/**
 * Ray cast result for clearance analysis
 */
export interface RayHit {
  direction: { x: number; y: number; z: number };
  distance: number;
  hitPoint?: { x: number; y: number; z: number };
  hitElementId?: number;
  hitElementName?: string;
}

/**
 * Clearance analysis result
 */
export interface ClearanceAnalysis {
  rays: RayHit[];
  boundingBox?: {
    min: { x: number; y: number; z: number };
    max: { x: number; y: number; z: number };
  };
  obb?: {
    center: { x: number; y: number; z: number };
    halfSizes: { x: number; y: number; z: number };
    rotation: { x: number; y: number; z: number; w: number };
  };
  nearestWallDistance?: number;
  surroundingWalls: {
    dbId: number;
    name: string;
    distance: number;
    direction: string;
  }[];
}

/**
 * Validation result for a single fire alarm device
 */
export interface FireAlarmValidationResult {
  deviceDbId: number;
  deviceName: string;
  deviceFamily?: string;
  level?: string;
  status: "pass" | "fail" | "warning" | "info";
  message: string;
  clearanceAnalysis?: ClearanceAnalysis;
  details?: {
    category?: string;
    raysCast?: number;
    wallsDetected?: number;
    minClearance?: number;
    [key: string]: any;
  };
}

/**
 * Complete validation report for fire alarm devices
 */
export interface FireAlarmValidationReport {
  metadata: {
    timestamp: Date;
    modelName?: string;
    totalDevices: number;
    parameters: FireAlarmValidationParams;
  };
  results: FireAlarmValidationResult[];
  summary: {
    passCount: number;
    failCount: number;
    warningCount: number;
    infoCount: number;
  };
}

/**
 * Parameters for fire alarm validation
 */
export interface FireAlarmValidationParams {
  deviceCategory: string;
  deviceFamilyFilter: string;
  maxRayDistance: number; // Maximum distance for ray casting in feet
  performClearanceAnalysis: boolean; // Whether to perform clearance analysis
  demoMode?: boolean; // When true, only analyzes two specific devices for demonstration
}

/**
 * Default parameters for fire alarm validation
 */
export const DEFAULT_FIRE_ALARM_PARAMS: FireAlarmValidationParams = {
  deviceCategory: "Revit Fire Alarm Devices",
  deviceFamilyFilter:
    "AEC_Indoor Horns, Strobes, & Horn Strobes for Wall Applications",
  maxRayDistance: 20, // 20 feet default
  performClearanceAnalysis: true, // Enabled with Fast mode for quick performance
  demoMode: true, // Default to demo mode (only analyze devices 70368 and 70370)
};
